// QuotePartsModal.tsx - Modal seguro con feedback visual mejorado
import React, { useState } from 'react';
import { X, AlertCircle, CheckCircle } from 'lucide-react';
import axios from 'axios';
import './QuoteModals.css';

interface Repuesto {
  pieza: string;
  cantidad: number;
  tipo: 'originales' | 'homologados' | 'ambos';
}

interface FormData {
  // Datos cliente
  nombre: string;
  rut: string;
  correo: string;
  telefono: string;
  
  // Datos equipo
  marca: string;
  modelo: string;
  configuracion: string;
  ano: string;
  chasis: string;
  patente: string;
  
  // Repuestos y observaciones
  repuestos: Repuesto[];
  observaciones: string;
  confirmaInfo: boolean;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const QuotePartsModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const [formData, setFormData] = useState<FormData>({
    nombre: '',
    rut: '',
    correo: '',
    telefono: '',
    marca: 'Caterpillar',
    modelo: '',
    configuracion: '',
    ano: '',
    chasis: '',
    patente: '',
    repuestos: [{
      pieza: '',
      cantidad: 1,
      tipo: 'homologados'
    }],
    observaciones: '',
    confirmaInfo: false
  });

  // ========================================
  // MANEJO DE REPUESTOS
  // ========================================
  const agregarRepuesto = () => {
    setFormData({
      ...formData,
      repuestos: [
        ...formData.repuestos,
        { pieza: '', cantidad: 1, tipo: 'homologados' }
      ]
    });
  };

  const eliminarRepuesto = (index: number) => {
    if (formData.repuestos.length > 1) {
      setFormData({
        ...formData,
        repuestos: formData.repuestos.filter((_, i) => i !== index)
      });
    }
  };

  const actualizarRepuesto = (index: number, campo: keyof Repuesto, valor: any) => {
    const nuevosRepuestos = [...formData.repuestos];
    nuevosRepuestos[index] = {
      ...nuevosRepuestos[index],
      [campo]: valor
    };
    setFormData({ ...formData, repuestos: nuevosRepuestos });
  };

  // ========================================
  // VALIDACIÓN
  // ========================================
  const validarFormulario = (): string | null => {
    // Campos obligatorios
    if (!formData.nombre.trim()) return 'El nombre es obligatorio';
    if (!formData.correo.trim()) return 'El correo es obligatorio';
    if (!formData.telefono.trim()) return 'El teléfono es obligatorio';
    if (!formData.modelo.trim()) return 'El modelo es obligatorio';
    
    // Validar email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.correo)) {
      return 'El correo electrónico no es válido';
    }
    
    // Validar al menos un repuesto con datos
    const tieneRepuestos = formData.repuestos.some(r => r.pieza.trim() !== '');
    if (!tieneRepuestos) {
      return 'Debes agregar al menos un repuesto';
    }
    
    // Validar que todos los repuestos con pieza tengan cantidad
    const repuestosInvalidos = formData.repuestos.some(
      r => r.pieza.trim() !== '' && (!r.cantidad || r.cantidad < 1)
    );
    if (repuestosInvalidos) {
      return 'Todos los repuestos deben tener una cantidad válida';
    }
    
    // Confirmar información
    if (!formData.confirmaInfo) {
      return 'Debes confirmar que la información es correcta';
    }
    
    return null;
  };

  // ========================================
  // ENVÍO DE FORMULARIO
  // ========================================
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError('');
    setSubmitSuccess(false);
    
    // Validar
    const error = validarFormulario();
    if (error) {
      setSubmitError(error);
      // Scroll al top para ver el error
      document.querySelector('.modal-content')?.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post('/public/quote-parts', {
        cliente: {
          nombre: formData.nombre,
          rut: formData.rut,
          correo: formData.correo,
          telefono: formData.telefono
        },
        equipo: {
          marca: formData.marca,
          modelo: formData.modelo,
          configuracion: formData.configuracion,
          ano: formData.ano,
          chasis: formData.chasis,
          patente: formData.patente
        },
        repuestos: formData.repuestos.filter(r => r.pieza.trim() !== ''),
        observaciones: formData.observaciones
      });

      if (response.data.success) {
        setSubmitSuccess(true);
        setSubmitError('');
        
        // Scroll al top para ver el éxito
        document.querySelector('.modal-content')?.scrollTo({ top: 0, behavior: 'smooth' });
        
        // Resetear y cerrar después de 3 segundos
        setTimeout(() => {
          resetFormulario();
          onClose();
          setSubmitSuccess(false);
        }, 3000);
      } else {
        throw new Error(response.data.error || 'Error al enviar cotización');
      }
    } catch (error: any) {
      console.error('Error enviando cotización:', error);
      setSubmitError(
        error.response?.data?.error || 
        error.message || 
        'Error al enviar la cotización. Intenta nuevamente.'
      );
      // Scroll al top para ver el error
      document.querySelector('.modal-content')?.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetFormulario = () => {
    setFormData({
      nombre: '',
      rut: '',
      correo: '',
      telefono: '',
      marca: 'Caterpillar',
      modelo: '',
      configuracion: '',
      ano: '',
      chasis: '',
      patente: '',
      repuestos: [{
        pieza: '',
        cantidad: 1,
        tipo: 'homologados'
      }],
      observaciones: '',
      confirmaInfo: false
    });
    setSubmitError('');
    setSubmitSuccess(false);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div 
        className="modal-content quote-modal" 
        onClick={(e) => e.stopPropagation()}
      >
        {/* HEADER */}
        <div className="modal-header">
          <h3>📋 Cotización de Repuestos</h3>
          <button className="modal-close" onClick={onClose} type="button">
            <X />
          </button>
        </div>

        {/* SUCCESS MESSAGE */}
        {submitSuccess && (
          <div className="alert alert-success">
            <CheckCircle size={24} />
            <span>✅ Cotización enviada correctamente. Te contactaremos pronto.</span>
          </div>
        )}

        {/* ERROR MESSAGE */}
        {submitError && (
          <div className="alert alert-error">
            <AlertCircle size={24} />
            <span>{submitError}</span>
          </div>
        )}

        {/* FORM */}
        <form onSubmit={handleSubmit} className="quote-form">
          
          {/* DATOS CLIENTE */}
          <div className="form-section">
            <h4 className="section-title">👤 Datos de Contacto</h4>
            
            <div className="form-row">
              <div className="form-group">
                <label>Nombre Completo *</label>
                <input
                  type="text"
                  value={formData.nombre}
                  onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                  placeholder="Juan Pérez"
                  required
                  disabled={isSubmitting}
                />
              </div>
              
              <div className="form-group">
                <label>RUT/ID</label>
                <input
                  type="text"
                  value={formData.rut}
                  onChange={(e) => setFormData({...formData, rut: e.target.value})}
                  placeholder="12.345.678-9"
                  disabled={isSubmitting}
                />
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label>Correo Electrónico *</label>
                <input
                  type="email"
                  value={formData.correo}
                  onChange={(e) => setFormData({...formData, correo: e.target.value})}
                  placeholder="correo@empresa.com"
                  required
                  disabled={isSubmitting}
                />
              </div>
              
              <div className="form-group">
                <label>Teléfono *</label>
                <input
                  type="tel"
                  value={formData.telefono}
                  onChange={(e) => setFormData({...formData, telefono: e.target.value})}
                  placeholder="+56 9 1234 5678"
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </div>

          {/* DATOS EQUIPO */}
          <div className="form-section">
            <h4 className="section-title">🚜 Datos del Equipo</h4>
            
            <div className="form-row">
              <div className="form-group">
                <label>Marca *</label>
                <select
                  value={formData.marca}
                  onChange={(e) => setFormData({...formData, marca: e.target.value})}
                  required
                  disabled={isSubmitting}
                >
                  <option value="Caterpillar">Caterpillar</option>
                  <option value="Komatsu">Komatsu</option>
                  <option value="Hitachi">Hitachi</option>
                  <option value="Volvo">Volvo</option>
                  <option value="Otro">Otro</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Modelo *</label>
                <input
                  type="text"
                  value={formData.modelo}
                  onChange={(e) => setFormData({...formData, modelo: e.target.value})}
                  placeholder="Ej: 793F, D11T, 789D"
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label>Configuración</label>
                <input
                  type="text"
                  value={formData.configuracion}
                  onChange={(e) => setFormData({...formData, configuracion: e.target.value})}
                  placeholder="Ej: Z4Z, L4G, AJ8"
                  disabled={isSubmitting}
                />
                <small className="form-hint">Código de configuración del equipo (si lo conoces)</small>
              </div>
              
              <div className="form-group">
                <label>Año</label>
                <input
                  type="number"
                  value={formData.ano}
                  onChange={(e) => setFormData({...formData, ano: e.target.value})}
                  placeholder="2020"
                  min="1990"
                  max={new Date().getFullYear() + 1}
                  disabled={isSubmitting}
                />
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label>N° Chasis/Serie</label>
                <input
                  type="text"
                  value={formData.chasis}
                  onChange={(e) => setFormData({...formData, chasis: e.target.value})}
                  placeholder="Ej: WJCPCBLCX11000237"
                  disabled={isSubmitting}
                />
              </div>
              
              <div className="form-group">
                <label>Patente (Opcional)</label>
                <input
                  type="text"
                  value={formData.patente}
                  onChange={(e) => setFormData({...formData, patente: e.target.value})}
                  placeholder="XXXX-00"
                  maxLength={7}
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </div>

          {/* REPUESTOS */}
          <div className="form-section">
            <h4 className="section-title">🔧 Repuestos a Cotizar</h4>
            
            {formData.repuestos.map((repuesto, index) => (
              <div key={index} className="repuesto-item">
                <div className="repuesto-header">
                  <span className="repuesto-numero">Repuesto #{index + 1}</span>
                  {formData.repuestos.length > 1 && (
                    <button
                      type="button"
                      className="btn-eliminar-repuesto"
                      onClick={() => eliminarRepuesto(index)}
                      disabled={isSubmitting}
                    >
                      ×
                    </button>
                  )}
                </div>
                
                <div className="form-group">
                  <label>Número de Parte / Descripción *</label>
                  <input
                    type="text"
                    value={repuesto.pieza}
                    onChange={(e) => actualizarRepuesto(index, 'pieza', e.target.value)}
                    placeholder="Ej: 123-4567 o 'Filtro de aceite'"
                    required
                    disabled={isSubmitting}
                  />
                  <small className="form-hint">
                    Ingresa el número de parte CAT o una descripción del repuesto
                  </small>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>Cantidad *</label>
                    <input
                      type="number"
                      value={repuesto.cantidad}
                      onChange={(e) => actualizarRepuesto(index, 'cantidad', parseInt(e.target.value) || 1)}
                      required
                      min="1"
                      max="999"
                      disabled={isSubmitting}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Tipo de repuesto *</label>
                    <div className="radio-group">
                      <label className="radio-label">
                        <input
                          type="radio"
                          name={`tipo-${index}`}
                          value="originales"
                          checked={repuesto.tipo === 'originales'}
                          onChange={(e) => actualizarRepuesto(index, 'tipo', e.target.value as any)}
                          disabled={isSubmitting}
                        />
                        Originales
                      </label>
                      <label className="radio-label">
                        <input
                          type="radio"
                          name={`tipo-${index}`}
                          value="homologados"
                          checked={repuesto.tipo === 'homologados'}
                          onChange={(e) => actualizarRepuesto(index, 'tipo', e.target.value as any)}
                          disabled={isSubmitting}
                        />
                        Homologados
                      </label>
                      <label className="radio-label">
                        <input
                          type="radio"
                          name={`tipo-${index}`}
                          value="ambos"
                          checked={repuesto.tipo === 'ambos'}
                          onChange={(e) => actualizarRepuesto(index, 'tipo', e.target.value as any)}
                          disabled={isSubmitting}
                        />
                        Ambos
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            
            <button 
              type="button" 
              className="btn-agregar-repuesto" 
              onClick={agregarRepuesto}
              disabled={isSubmitting}
            >
              + Agregar Otro Repuesto
            </button>
          </div>

          {/* OBSERVACIONES */}
          <div className="form-group">
            <label>Observaciones Adicionales</label>
            <textarea
              value={formData.observaciones}
              onChange={(e) => setFormData({...formData, observaciones: e.target.value})}
              rows={3}
              placeholder="Detalles adicionales, urgencia, condiciones especiales..."
              disabled={isSubmitting}
            />
          </div>

          {/* CONFIRMACIÓN */}
          <div className="form-checkbox">
            <label>
              <input
                type="checkbox"
                checked={formData.confirmaInfo}
                onChange={(e) => setFormData({...formData, confirmaInfo: e.target.checked})}
                required
                disabled={isSubmitting}
              />
              Declaro que la información entregada es correcta
            </label>
          </div>

          {/* SUBMIT */}
          <button 
            type="submit" 
            className="btn-modal-submit" 
            disabled={isSubmitting || submitSuccess}
          >
            {isSubmitting ? 'Enviando...' : submitSuccess ? '✅ Enviado' : 'Enviar Cotización'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default QuotePartsModal;